import { inject, Injectable } from '@angular/core';
import { PermissionsList } from 'src/app/shared/common/permissionsList.enum';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  readonly #authService = inject(AuthService);
  private userPermissions: number[] = [];

  constructor() {
    this.loadPermissions();
  }

  /**
   * Loads permissions from `localStorage` and initializes `userPermissions`.
   */
  private loadPermissions(): void {
    try {
      const userInfo = this.#authService.currentUser();
      this.userPermissions = userInfo?.permissionList || [];
    } catch (error) {
      console.error('Error loading user permissions:', error);
      this.userPermissions = [];
    }
  }

  /**
   * Checks if the user has a specific permission.
   * @param permission The permission key from `PermissionsList`.
   * @returns `true` if the user has the specified permission, otherwise `false`.
   */
  hasPermission(permission: keyof typeof PermissionsList): boolean {
    const permissionValue = PermissionsList[permission];
    return this.userPermissions.includes(permissionValue);
  }

  /**
   * Checks if the user has any of the provided permissions.
   * @param permissions An array of permission keys from `PermissionsList`.
   * @returns `true` if the user has at least one of the specified permissions, otherwise `false`.
   */
  hasAnyPermission(permissions: (keyof typeof PermissionsList)[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  /**
   * Reloads permissions from `localStorage`.
   * Can be used if user permissions are updated during runtime.
   */
  refreshPermissions(): void {
    this.loadPermissions();
  }
}
